import { UserGroup02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { Layers, LayoutPanelTop } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CustomerModal } from "@/components/modals/customer-modal";
import { LoyaltyPointsModal } from "@/components/modals/loyalty-points-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DisplaySelect } from "@/components/ui/display-select";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { type Filter, Filters } from "@/components/ui/filters";
import { SearchInput } from "@/components/ui/updated-search-input";
import { CustomersTable } from "@/features/customers/_components/customers-table";
import { useCursorPaginatedQuery } from "@/hooks/use-cursor-paginated-query";
import { useDebounce } from "@/hooks/use-debounce";
import { useFilters } from "@/hooks/use-filters";
import { useLocalStorageSettings } from "@/hooks/use-local-storage-settings";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { handleConvexError } from "@/lib/errors";
import type { CustomerInput } from "@/lib/schemas/customer";
import { toast } from "@/lib/toast";
import type { Customer } from "@/lib/types/customer";
import { formatFullName } from "@/lib/utils";

export const Route = createFileRoute("/(dashboard)/customers")({
	component: CustomersPage,
});

const PAGE_SIZE = 50;

// Query type detection helpers
const isPhoneQuery = (q: string) => {
	const cleaned = q.replaceAll(/[\s-]/g, "");
	return /^[+0]/.test(cleaned) || /^\d{8,}$/.test(cleaned);
};

const isDocumentIdQuery = (q: string) => {
	// Strip whitespace AND hyphens so license formats like "TH-MED-2026-001"
	// route through the indexed lookups.
	const cleaned = q.replaceAll(/[\s-]/g, "");
	// Alphanumeric, 5+ chars, at least one letter (to distinguish from phone)
	return /^[A-Z0-9]{5,}$/i.test(cleaned) && /[A-Z]/i.test(cleaned);
};

// Matches both the legacy `M0050` format and the current generator's
// `MEM-000001` output (see `generateMemberNumber` in the backend domain).
const isMemberNoQuery = (q: string) => /^M(?:EM)?-?\d{4,}$/i.test(q.trim());

interface DisplaySettings {
	viewMode: "cards" | "rows";
	showHeaders: boolean;
	sortKey: string;
	columnVisibility: Record<string, boolean>;
}

const DEFAULT_SETTINGS: DisplaySettings = {
	viewMode: "rows",
	showHeaders: true,
	sortKey: "date",
	columnVisibility: {
		name: true,
		phone: true,
		age: true,
		registrationDate: true,
		type: true,
		points: true,
		totalSpent: true,
		visits: true,
		licenseNumber: false,
		documentId: false,
		memberNo: false,
	},
};

function CustomersPage() {
	// Get current user to find active organization
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	// State
	const [searchQuery, setSearchQuery] = useState("");
	const debouncedSearch = useDebounce(searchQuery.trim(), 300);

	const { resetPage: originalResetPage } = usePagination(PAGE_SIZE);

	const {
		settings: displaySettings,
		hasChanges,
		handleReset,
		handleSetDefault,
		updateSetting,
		updateColumnVisibility,
	} = useLocalStorageSettings<DisplaySettings>(
		"slappos_customers_display_settings",
		DEFAULT_SETTINGS,
		() => {
			const defaultMode =
				globalThis.window !== undefined && window.innerWidth < 640
					? "cards"
					: "rows";
			return { ...DEFAULT_SETTINGS, viewMode: defaultMode };
		},
	);

	// Modal state
	const [customerModalOpen, setCustomerModalOpen] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
		null,
	);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(
		null,
	);
	const [pointsModalOpen, setPointsModalOpen] = useState(false);
	const [customerForPoints, setCustomerForPoints] = useState<Customer | null>(
		null,
	);

	// Mutations
	const createCustomer = useMutation(api.features.customers.web.create);
	const updateCustomer = useMutation(api.features.customers.web.update);
	const deleteCustomer = useMutation(api.features.customers.web.remove);
	const updateLoyalty = useMutation(api.features.customers.web.updateLoyalty);

	// Mutation loading states
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isAdjustingPoints, setIsAdjustingPoints] = useState(false);

	// Build filters
	const customerFilters: Filter[] = useMemo(
		() => [
			{
				key: "status",
				label: "Status",
				icon: <Layers className="size-4" />,
				options: [
					{ value: "active", label: "Active" },
					{ value: "inactive", label: "Inactive" },
				],
			},
		],
		[],
	);

	const filterState = useFilters({
		filters: customerFilters,
	});

	// Get selected filters
	const selectedStatus = useMemo(() => {
		const statusFilter = filterState.activeFilters.find(
			(f) => f.key === "status",
		);
		if (!statusFilter) return undefined;
		const value = Array.isArray(statusFilter.value)
			? statusFilter.value[0]
			: statusFilter.value;
		if (value === "active") return true;
		if (value === "inactive") return false;
		return undefined;
	}, [filterState.activeFilters]);

	// Unified list query with cursor pagination
	const customersList = useCursorPaginatedQuery(
		api.features.customers.web.list,
		orgId && !debouncedSearch
			? {
					orgId,
					activeOnly: selectedStatus === true ? true : undefined,
				}
			: "skip",
		{ pageSize: PAGE_SIZE },
	);

	useEffect(() => {
		customersList.reset();
		originalResetPage();
	}, [customersList.reset, originalResetPage]);

	const customersSearchQuery = useQuery(
		api.features.customers.web.search,
		orgId && debouncedSearch && !isPhoneQuery(debouncedSearch)
			? {
					orgId,
					query: debouncedSearch,
					activeOnly: selectedStatus === true ? true : undefined,
				}
			: "skip",
	);

	// Phone lookup query
	const phoneQuery = useQuery(
		api.features.customers.web.findByPhone,
		orgId && debouncedSearch && isPhoneQuery(debouncedSearch)
			? { orgId, phone: debouncedSearch.replaceAll(/[\s-]/g, "") }
			: "skip",
	);

	// Document ID lookup query — also fans out to license # for the same alphanumeric query
	const documentIdQuery = useQuery(
		api.features.customers.web.findByDocumentId,
		orgId && debouncedSearch && isDocumentIdQuery(debouncedSearch)
			? { orgId, documentId: debouncedSearch.trim() }
			: "skip",
	);

	const licenseNumberQuery = useQuery(
		api.features.customers.web.findByLicenseNumber,
		orgId && debouncedSearch && isDocumentIdQuery(debouncedSearch)
			? { orgId, licenseNumber: debouncedSearch.trim() }
			: "skip",
	);

	const memberNoQuery = useQuery(
		api.features.customers.web.findByMemberNo,
		orgId && debouncedSearch && isMemberNoQuery(debouncedSearch)
			? { orgId, memberNo: debouncedSearch.trim() }
			: "skip",
	);

	const customers = useMemo(() => {
		if (!debouncedSearch) {
			return customersList.data as Customer[];
		}

		// Phone lookup takes priority
		if (isPhoneQuery(debouncedSearch) && phoneQuery !== undefined) {
			return phoneQuery ? [phoneQuery as Customer] : [];
		}

		// Member No. is a strict format (MEM-######) — short-circuit before the
		// looser document-ID branch which would otherwise also match.
		if (isMemberNoQuery(debouncedSearch) && memberNoQuery !== undefined) {
			return memberNoQuery ? [memberNoQuery as Customer] : [];
		}

		// Document ID / license # lookup — return whichever resolved.
		// If both return null, fall through to name search (input may be a name
		// that happens to look like an ID, e.g. "Oliver").
		if (isDocumentIdQuery(debouncedSearch)) {
			if (documentIdQuery !== undefined && documentIdQuery !== null) {
				return [documentIdQuery as Customer];
			}
			if (licenseNumberQuery !== undefined && licenseNumberQuery !== null) {
				return [licenseNumberQuery as Customer];
			}
		}

		// Fall back to name search
		return (customersSearchQuery ?? []) as Customer[];
	}, [
		debouncedSearch,
		customersList.data,
		customersSearchQuery,
		phoneQuery,
		documentIdQuery,
		licenseNumberQuery,
		memberNoQuery,
	]);

	const isLoading = useMemo(() => {
		if (!debouncedSearch) {
			return customersList.isLoading;
		}
		if (isPhoneQuery(debouncedSearch)) {
			return phoneQuery === undefined;
		}
		if (isMemberNoQuery(debouncedSearch)) {
			return memberNoQuery === undefined;
		}
		if (isDocumentIdQuery(debouncedSearch)) {
			// Wait for ID lookups; if both resolved null, the memo above falls
			// through to name search — wait for that too.
			if (documentIdQuery === undefined || licenseNumberQuery === undefined) {
				return true;
			}
			if (documentIdQuery !== null || licenseNumberQuery !== null) {
				return false;
			}
		}
		return customersSearchQuery === undefined;
	}, [
		debouncedSearch,
		customersList.isLoading,
		customersSearchQuery,
		phoneQuery,
		documentIdQuery,
		licenseNumberQuery,
		memberNoQuery,
	]);

	// Handlers
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchQuery(value);
			customersList.reset();
		},
		[customersList],
	);

	const handleCreate = useCallback(() => {
		setSelectedCustomer(null);
		setCustomerModalOpen(true);
	}, []);

	const handleEdit = useCallback((customer: Customer) => {
		setSelectedCustomer(customer);
		setCustomerModalOpen(true);
	}, []);

	const handleDelete = useCallback((customer: Customer) => {
		setCustomerToDelete(customer);
		setDeleteDialogOpen(true);
	}, []);

	const handleAdjustPoints = useCallback((customer: Customer) => {
		setCustomerForPoints(customer);
		setPointsModalOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!customerToDelete || !orgId) return;

		setIsDeleting(true);
		try {
			await deleteCustomer({ orgId, customerId: customerToDelete._id });
			toast.success(<>Customer deleted</>);
			setDeleteDialogOpen(false);
			setCustomerToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to delete customer");
		} finally {
			setIsDeleting(false);
		}
	}, [customerToDelete, orgId, deleteCustomer]);

	const handleCustomerSubmit = useCallback(
		async (data: CustomerInput) => {
			if (!orgId) return;

			setIsSubmitting(true);
			try {
				// Base customer data (shared between create and update)
				const baseCustomerData = {
					orgId,
					firstName: data.firstName,
					lastName: data.lastName || undefined,
					phone: data.phone || undefined,
					email: data.email || undefined,
					birthday: data.birthday || undefined,
					gender: data.gender,
					nationality: data.nationality || undefined,
					region: data.region || undefined,
					documentId: data.documentId || undefined,
					licenseNumber: data.licenseNumber || undefined,
					lineId: data.lineId || undefined,
					referralSource: data.referralSource || undefined,
					type: data.type,
				};

				if (selectedCustomer) {
					// Update includes isActive (to allow status changes)
					await updateCustomer({
						...baseCustomerData,
						customerId: selectedCustomer._id,
						isActive: data.isActive,
					});
					toast.success(<>Customer updated</>);
				} else {
					// Create doesn't include isActive or memberNo (backend auto-sets them)
					await createCustomer(baseCustomerData);
					toast.success(<>Customer created</>);
				}
			} catch (error) {
				handleConvexError(
					error,
					selectedCustomer
						? "Failed to update customer"
						: "Failed to create customer",
				);
				throw error;
			} finally {
				setIsSubmitting(false);
			}
		},
		[orgId, selectedCustomer, createCustomer, updateCustomer],
	);

	const handlePointsSubmit = useCallback(
		async (customerId: Customer["_id"], adjustment: number) => {
			if (!orgId) return;

			setIsAdjustingPoints(true);
			try {
				if (adjustment > 0) {
					await updateLoyalty({
						orgId,
						customerId,
						addPoints: adjustment,
					});
				} else {
					await updateLoyalty({
						orgId,
						customerId,
						subtractPoints: Math.abs(adjustment),
					});
				}
				toast.success(
					<>Points {adjustment > 0 ? "added" : "subtracted"} successfully</>,
				);
			} catch (error) {
				handleConvexError(error, "Failed to adjust points");
				throw error;
			} finally {
				setIsAdjustingPoints(false);
			}
		},
		[orgId, updateLoyalty],
	);

	// Apply sorting before pagination (Note: Client-side sorting only applies to the current page)
	const sortedCustomers = useMemo(() => {
		return [...customers].sort((a, b) => {
			if (displaySettings.sortKey === "date") {
				return (b._creationTime || 0) - (a._creationTime || 0);
			}
			if (displaySettings.sortKey === "points") {
				return (b.loyaltyPoints || 0) - (a.loyaltyPoints || 0);
			}
			if (displaySettings.sortKey === "totalSpent") {
				return (b.totalSpentSatangs || 0) - (a.totalSpentSatangs || 0);
			}
			if (displaySettings.sortKey === "visits") {
				return (b.visitCount || 0) - (a.visitCount || 0);
			}
			return 0;
		});
	}, [customers, displaySettings.sortKey]);

	return (
		<PageContent
			title="Customers"
			controls={
				<Can scope="customers.create">
					<ActionHeaderButton label="Add Customer" onClick={handleCreate} />
				</Can>
			}
		>
			<div className="flex flex-1 flex-col gap-4 sm:gap-6">
				{/* Search and Filters */}
				{!isLoading &&
					(sortedCustomers.length > 0 ||
						searchQuery ||
						filterState.activeFilters.length > 0) && (
						<>
							<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
								<div className="flex-1 sm:max-w-sm">
									<SearchInput
										placeholder="Search by name, phone, or ID..."
										value={searchQuery}
										onChange={(e) => handleSearchChange(e.target.value)}
									/>
								</div>
								<div className="flex items-center gap-2">
									<Filters.Trigger
										filters={filterState.filters}
										activeFilters={filterState.activeFilters}
										onSelect={filterState.onSelect}
										onRemove={filterState.onRemove}
									/>
									<DisplaySelect
										viewMode={displaySettings.viewMode}
										onViewModeChange={(mode) => updateSetting("viewMode", mode)}
										selectedSort={displaySettings.sortKey}
										onSortChange={(sort) => updateSetting("sortKey", sort)}
										sortOptions={[
											{ value: "date", label: "Date (Newest)" },
											{ value: "points", label: "Loyalty Points" },
											{ value: "totalSpent", label: "Total Spent" },
											{ value: "visits", label: "Visit Count" },
										]}
										options={[
											{
												key: "showHeaders",
												label: "Show table headers",
												checked: displaySettings.showHeaders,
												icon: <LayoutPanelTop className="size-4" />,
											},
										]}
										onOptionsChange={(key, checked) => {
											if (key === "showHeaders")
												updateSetting("showHeaders", checked);
										}}
										properties={[
											{
												key: "phone",
												label: "Phone",
												visible: displaySettings.columnVisibility.phone,
											},
											{
												key: "age",
												label: "Age",
												visible: displaySettings.columnVisibility.age,
											},
											{
												key: "registrationDate",
												label: "Registration Date",
												visible:
													displaySettings.columnVisibility.registrationDate,
											},
											{
												key: "type",
												label: "Type",
												visible: displaySettings.columnVisibility.type,
											},
											{
												key: "points",
												label: "Points",
												visible: displaySettings.columnVisibility.points,
											},
											{
												key: "totalSpent",
												label: "Total Spent",
												visible: displaySettings.columnVisibility.totalSpent,
											},
											{
												key: "visits",
												label: "Visits",
												visible: displaySettings.columnVisibility.visits,
											},
											{
												key: "licenseNumber",
												label: "License #",
												visible: displaySettings.columnVisibility.licenseNumber,
											},
											{
												key: "documentId",
												label: "Document ID",
												visible: displaySettings.columnVisibility.documentId,
											},
											{
												key: "memberNo",
												label: "Member No.",
												visible: displaySettings.columnVisibility.memberNo,
											},
										]}
										onPropertiesChange={updateColumnVisibility}
										onReset={handleReset}
										onSetDefault={handleSetDefault}
										showFooter={hasChanges}
									/>
								</div>
							</div>

							{/* Active Filter Pills */}
							<Filters.List
								filters={filterState.filterListItems}
								onRemove={filterState.onRemove}
								onClear={filterState.onClear}
							/>
						</>
					)}
				{/* Customers Table or Empty State */}
				{!isLoading &&
				sortedCustomers.length === 0 &&
				customersList.pageIndex === 0 ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No customers found"
							description="Add customers to your database to track their visits, loyalty points, and preferences"
							actionLabel="Add Customer"
							onAction={handleCreate}
							icon={
								<HugeiconsIcon
									icon={UserGroup02Icon}
									className="h-5 w-5 text-neutral-700"
									strokeWidth={2.2}
								/>
							}
						/>
					</div>
				) : (
					<CustomersTable
						data={sortedCustomers}
						loading={isLoading}
						{...(debouncedSearch ? {} : customersList.tableProps)}
						onRowClick={handleEdit}
						onEdit={handleEdit}
						onDelete={handleDelete}
						onAdjustPoints={handleAdjustPoints}
						viewMode={displaySettings.viewMode}
						showHeaders={displaySettings.showHeaders}
						columnVisibility={displaySettings.columnVisibility}
						emptyState={
							<div className="flex flex-col items-center gap-2 py-8 text-center px-4">
								<span className="text-neutral-500 font-medium whitespace-normal wrap-break-word max-w-[250px]">
									{debouncedSearch
										? `No customers found for "${searchQuery}"`
										: customersList.pageIndex === 0
											? "No customers found"
											: "No more customers to show"}
								</span>
								{!debouncedSearch && customersList.pageIndex === 0 && (
									<span className="text-sm text-neutral-400">
										Add your first customer to get started
									</span>
								)}
								{debouncedSearch && (
									<span className="text-sm text-neutral-400">
										Try adjusting your search or filters
									</span>
								)}
							</div>
						}
					/>
				)}
			</div>

			{/* Customer Modal (Create/Edit) */}
			<CustomerModal
				open={customerModalOpen}
				onOpenChange={setCustomerModalOpen}
				customer={selectedCustomer}
				orgId={orgId}
				onSubmit={handleCustomerSubmit}
				isSubmitting={isSubmitting}
			/>

			{/* Loyalty Points Modal */}
			<LoyaltyPointsModal
				open={pointsModalOpen}
				onOpenChange={setPointsModalOpen}
				customer={customerForPoints}
				orgId={orgId}
				onSubmit={handlePointsSubmit}
				isSubmitting={isAdjustingPoints}
			/>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete customer?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-medium text-neutral-900">
								"
								{formatFullName(
									customerToDelete?.firstName,
									customerToDelete?.lastName,
								)}
								"?
							</span>{" "}
							This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
